import { FC, ReactNode, useEffect, useState, useRef } from 'react';
import { isPlatform } from '../utils/isPlatform';
import { useIfLoggedIn } from '../utils/useIfLoggedIn';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { AlertPresenter } from '../AlertBinder';
import { CurrentRecordingStatus, RecordingData, VoiceRecorder } from '@independo/capacitor-voice-recorder';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { base64ToArrayBuffer } from '../utils/Audio';
import { useSubscription } from '@meraki-internal/state';
import { RecordingState } from './RecordingState';
import { LiveUpdatesService } from '@meraki-internal/live-updates';
import { CareMapState } from '../caremap/CareMapState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { RevenueCatModel } from '../innerhive-plus/revenue-cat/RevenueCatModel';
import { InnerhivePlusDialogViewModel } from '../innerhive-plus/InnerhivePlusDialogViewModel';
import { CaremapNotesModelFactory } from '../notes/model/CaremapNotesModelFactory';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { RecordingSheet } from './RecordingSheet';
import { useElapsedTimer } from '../utils/useElapsedTimer';
import { Logger } from '../support/Logger';
import { DevSettings } from '../support/DevSettings';
import { NetworkAdapter } from '../support/NetworkAdapter';
import { RecordingConsentModal } from './RecordingConsentModal';
import { IRecordingContext } from './RecordingEmptyState';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { RECORDING_ABORTED } from './RecordingService';

export const useCanRecord = () => {
    const careMapState = useInstance(CareMapState);
    const careMap = careMapState.getMostRecentCaremap();
    const isViewer = careMap && !careMap?.links?.self?.actions.includes('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    return !isViewer || (isAnonymous && !careMap);
};

export const Recorder: FC<{
    children?: ReactNode;
    trackingFrom: 'Record Widget' | 'Record Nav';
    includeSheet?: boolean;
    cannotRecordClickHandler?: () => void;
}> = ({
    children,
    trackingFrom,
    includeSheet,
    cannotRecordClickHandler
}) => {
    const ifLoggedIn = useIfLoggedIn();
    const [isConsentModalOpen, setIsConsentModalOpen] = useState(false);
    const [consentModalKey, setConsentModalKey] = useState(0);
    const consentResolveRef = useRef<((value: boolean) => void) | null>(null);
    const consentAcceptedRef = useRef<boolean>(false);

    // Identifies the in-flight start so a superseded one can bow out when its mic finally opens.
    const startGenerationRef = useRef<number>(0);

    // when startTime is called (on pause), callback happens 30 minutes later
    // unless stopTimer is called beforehand (resume, stop / discard etc...)
    // Use shorter timeout when short pause auto-save is enabled (for E2E tests)
    const devSettings = useInstance(DevSettings);
    const autoSaveTimeout = devSettings.recordingShortPauseAutoSave ? 5000 : 1800000;
    const { startTimer, stopTimer } = useElapsedTimer(() => stopRecording({ autoSaved: true }), autoSaveTimeout);

    const logger = useInstance(Logger);
    const careMapState = useInstance(CareMapState);
    const history = useInstance(HistoryViewModel);
    const alert = useInstance(AlertPresenter);
    const liveUpdatesService = useInstance(LiveUpdatesService);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const networkAdapter = useInstance(NetworkAdapter);
    const events = useInstance(BufferedEventBus);

    const canRecord = useCanRecord();

    // Generic abort signal — fires whenever a recording flow exits without committing.
    // Listeners self-filter via their own state; Recorder does not need to know which feature
    // initiated the recording.
    const emitRecordingAborted = () => {
        events.emit(RECORDING_ABORTED, undefined);
    };

    const recordingState = useInstance(RecordingState);
    useSubscription(() => recordingState);

    const notesModelFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesModelFactory);

    const innerhivePlusDialogVM = useInstance(InnerhivePlusDialogViewModel);
    const revenueCat = useInstance(RevenueCatModel);
    useSubscription(() => revenueCat);

    // TODO: forActiveCaremap should maybe return underined or something instead of throwing an error?
    //      we want this to show up if the user is anonymous still
    let notesModel = undefined;
    try {
        notesModel = notesModelFactory.forActiveCaremap();
    } catch (e) {
        // no active caremap
    }

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const shouldPromptForInnerhivePlus = !isAnonymous && !revenueCat.hasInnerhivePlus() && recordingState.requiresInnerhivePlus(revenueCat.getFreeTierLimits());

    // Ready to record once notes have loaded (needed to detect intro flow).
    // When there's no active caremap, notesModel is undefined and we're ready
    // (ensureCareMapExists will create one).
    const isReadyToRecord = !notesModel || notesModel.hasLoaded;

    useEffect(() => {
        if (recordingState.state.recordingInterrupted) {
            // cleanup, the voice recorder plugin / os actually already stopped it but we don't think it's stopped
            stopRecording();

            mixpanel.track('Recording Interrupted', () => ({
                'Cause': 'phone call'
            }));
        }
    }, [recordingState.state.recordingInterrupted]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (recordingState.state.showRecordingInterruptedAlert) {
            showRecordingInterruptedAlert();
        }
    }, [recordingState.state.showRecordingInterruptedAlert]); // eslint-disable-line react-hooks/exhaustive-deps

    // so things like buttons in Alerts can use the Recorder
    useEffect(() => {
        if (recordingState.state.forceStartRecording && isReadyToRecord) {
            clickHandler();
            recordingState.setState({ forceStartRecording: false });
        }
    }, [recordingState.state.forceStartRecording, isReadyToRecord]); // eslint-disable-line react-hooks/exhaustive-deps

    const showRecordingInterruptedAlert = async () => {
        recordingState.setState({
            recordingInterrupted: false,
            recordingInterruptedOutgoing: false,
            showRecordingInterruptedAlert: false
        });

        const action = await alert.showAlertV2({
            header: 'Recording Interrupted!',
            message: 'Your previous recording was interrupted and has been saved. Would you like to start a new recording?',
            options: [{
                id: 'cancel',
                role: 'cancel',
                label: 'No thanks'
            }, {
                id: 'continue',
                label: 'Yes, record'
            }]
        });

        mixpanel.track('Recording Interruption  Prompt Acknowledged', () => ({
            'Response': action === 'continue' ? 'Continued' : 'Cancelled'
        }));

        if (action === 'continue') {
            await startRecording();
        }
    };

    // TODO: test
    // if user can't buy innerhive+ (e.g. a viewer) don't show the button at all
    if (shouldPromptForInnerhivePlus && !revenueCat.canHaveInnerhivePlus()) {
        return null;
    }

    const handleInnerhivePlus = async () => {
        if (!notesModel || !shouldPromptForInnerhivePlus) {
            return true;
        } else {
            const triggerSource = recordingState.getInnerhivePlusTriggerSource();
            const nowHasInnerhivePlus = await innerhivePlusDialogVM.requirePurchase({ triggerSource });
            if (nowHasInnerhivePlus) {
                return true;
            } else {
                return false;
            }
        }
    };

    const askForConsent = async (): Promise<boolean> => {
        return new Promise((resolve) => {
            setConsentModalKey(prev => prev + 1);
            setIsConsentModalOpen(true);
            consentResolveRef.current = resolve;
        });
    };

    // Deliberately leaves the modal open — startRecording() closes it once the mic is actually
    // capturing, so the user is never looking at a "ready" screen while audio is still being lost.
    const handleConsentAccept = () => {
        const recordingContextType = recordingState.getCurrentRecordingContext();
        consentAcceptedRef.current = true;
        mixpanel.track('Recording Consent Acknowledged', () => ({
            'Response': 'Continued',
            'Context': recordingContextType || 'unknown'
        }));
        if (consentResolveRef.current) {
            consentResolveRef.current(true);
            consentResolveRef.current = null;
        }
    };

    const handleConsentDecline = () => {
        const recordingContextType = recordingState.getCurrentRecordingContext();
        consentAcceptedRef.current = false;
        setIsConsentModalOpen(false);
        mixpanel.track('Recording Consent Acknowledged', () => ({
            'Response': 'Cancelled',
            'Context': recordingContextType || 'unknown'
        }));
        if (consentResolveRef.current) {
            consentResolveRef.current(false);
            consentResolveRef.current = null;
        }
    };

    const handleConsentModalDismiss = () => {
        // Only clear recording context if user declined
        // If they accepted, keep the context for the recording sheet tips
        if (!consentAcceptedRef.current) {
            recordingState.updateCurrentRecordingExtras({ recordingContext: undefined, summaryPrompt: undefined });
        }
        // Reset for next time
        consentAcceptedRef.current = false;
    };

    const getCurrentContext = (): IRecordingContext => {
        const recordingContextType = recordingState.getCurrentRecordingContext();

        // When no context is set (menu bar recording), use 'Capture Notes Now'
        if (!recordingContextType) {
            return {
                label: 'Quick capture',
                consentHeader: 'Capture Notes Now',
                consentMessage: 'Share what you remember—every detail helps create a complete picture.'
            };
        }

        return {
            type: recordingContextType,
            label: recordingContextType === 'at-appointment' ? 'At an appointment' : 'Just got back from appt',
            consentHeader: recordingContextType === 'at-appointment' ? 'Ready to Capture your Appointment?' : 'Capture Notes Now',
            consentMessage: recordingContextType === 'at-appointment' ? 'Make sure everyone present gives permission to be recorded.' : 'Share what you remember—every detail helps create a complete picture.'
        };
    };

    const processPermissions = async () => {
        const permissionState = await VoiceRecorder.requestAudioRecordingPermission();

        if (!permissionState.value) {
            let subHeader = '';
            let message = '';
            if (isPlatform('ios')) {
                subHeader = 'You need to allow full access to your Contacts to use them in Innerhive.';
                message = 'Tap "Open Settings" below, then enable "Microphone" access for Innerhive.';
            } else if (isPlatform('android')) {
                subHeader = 'You need to allow access to your Microphone to use appointment recording in Innerhive.';
                message = 'Tap "Open Settings" below, then select Permissions > Microphone and choose "Allow".';
            }
            const action = await alert.showAlertV2({
                header: 'Access to Microphone Disabled',
                subHeader: subHeader,
                message: message,
                backdropDismiss: true,
                options: [{
                    id: 'cancel',
                    label: 'No Thanks',
                    role: 'cancel'
                }, {
                    id: 'continue',
                    label: 'Open Settings'
                }]
            });

            if (action === 'continue') {
                NativeSettings.open({ optionIOS: IOSSettings.App, optionAndroid: AndroidSettings.ApplicationDetails });
            }

            return false;
        }

        return true;
    };

    const startRecording = async () => {
        const generation = ++startGenerationRef.current;

        try{
            // Owned here rather than by the callers so every entry point gets the busy state —
            // the interrupted-recording alert starts a recording without going through clickHandler.
            // Inside the try so no throw below can strand it and permanently disable the record control.
            recordingState.setStartingRecording(true);

            // Not awaited — lets care map creation run in parallel with VoiceRecorder startup.
            // RecordingService.add() awaits ensureCareMapExists() before accessing the caremap.
            careMapState.ensureCareMapExists();

            // NOTE: store locally? "greatly increases performance"
            /*
                Start the audio recording.
                options?: RecordingOptions
                Optional options can be used with this method to save the file in the device's filesystem and return a uri to that file instead of a base64 string. This greatly increases performance for large files.
            */

            liveUpdatesService.disable();

            // Nothing is captured until this resolves — on web it runs a permission query,
            // then getUserMedia, then spins up a MediaRecorder, which together take ~1.5s.
            // start() only runs afterwards so the sheet (bound to isRecording) and the elapsed
            // timer both begin at the same moment the audio does.
            await VoiceRecorder.startRecording();

            // A newer start superseded this one while the mic was coming up; it owns the plugin now.
            if (generation !== startGenerationRef.current) {
                return;
            }

            // stop() clears isStartingRecording, so losing the flag means this start was torn down
            // mid-acquisition (a phone call interrupting it, or a discard). Committing start() now
            // would resurrect a recording that no longer exists, so stop the mic we just opened.
            if (!recordingState.isStartingRecording()) {
                await VoiceRecorder.stopRecording();
                return;
            }

            recordingState.start();

            mixpanel.track('Recording Started', () => ({
                'tipsShown': recordingState.shouldShowTips(),
                'context': recordingState.getCurrentRecordingContext() || 'none'
            }));
        } catch (error) {
            // A superseded start failing says nothing about the one that replaced it, so leave
            // that one's session and UI alone.
            if (generation === startGenerationRef.current) {
                // start() may already have committed above, so roll the session back rather than
                // leaving a sheet and a running timer for a recording that is being torn down.
                recordingState.stop();

                // Tear the startup UI down before the error surfaces, otherwise the alert is
                // presented over a consent modal that then dismisses underneath it.
                setIsConsentModalOpen(false);
            }

            liveUpdatesService.enable();

            // log to Sentry if error is not about permissions
            if ((error as Error).message !== 'MISSING_PERMISSION') {
                alert.showAndLogError(error);
            }

            // if there is an error in the try (like when liveUpdatesService was in there and erroing out),
            // the recording still continues, so we need to (delayed) stop it
            await new Promise(resolve => setTimeout(
                () => resolve(VoiceRecorder.stopRecording()), 500
            ));
        } finally {
            // Guarantees the startup UI is torn down on every exit path, but only by the start that
            // still owns it. On success start() has already cleared the flag in the same update that
            // opened the sheet, so the meaningful work here is closing the consent modal.
            if (generation === startGenerationRef.current) {
                recordingState.setStartingRecording(false);
                setIsConsentModalOpen(false);
            }
        }
    };

    const pauseRecording = async () => {
        recordingState.pause();

        try {
            let result: CurrentRecordingStatus;
            try {
                result = await VoiceRecorder.getCurrentStatus();

                if (result.status === 'RECORDING') {
                    const paused = await VoiceRecorder.pauseRecording();
                    if (!paused.value) {
                        // pause was unsuccessful, tell state
                        recordingState.resume();
                    }
                } else { // result.status === 'NONE'
                    // log error to sentry, why would we get here?
                    logger.error(`Tryng to pause recording, result.status is: ${result.status}`);
                }

                startTimer();
            } catch (error) {
                if ((error as Error).message === 'RECORDING_HAS_NOT_STARTED') {
                    // if the user pauses too quickly, plugin still thinks a recording has started
                    const recorderResult = await new Promise(resolve => setTimeout(
                        () => resolve(VoiceRecorder.pauseRecording()), 500
                    ));
                    console.log(recorderResult);
                } else {
                    throw error;
                }
            }
        } catch (error) {
            // log to Sentry
            alert.showAndLogError(error);
        }
    };

    const onTimerPause = (totalSeconds: number) => {
        mixpanel.track('Recording Paused', () => {
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;
            return {
                'Duration': `${minutes < 1 ? '' : minutes+'m' } ${seconds}s`,
                'Duration Seconds': totalSeconds
            };
        });
    };

    const resumeRecording = async () => {
        recordingState.resume();

        try {
            let result: CurrentRecordingStatus;
            try {
                result = await VoiceRecorder.getCurrentStatus();

                if (result.status === 'PAUSED') {
                    const resumed = await VoiceRecorder.resumeRecording();
                    if (!resumed.value) {
                        // resume was unsuccessful, tell state
                        recordingState.pause();
                    }
                } else { // result.status === 'NONE'
                    // log error to sentry, why would we get here?
                    logger.error(`Tryng to resume recording, result.status is: ${result.status}`);
                }

                mixpanel.track('Recording Resumed');

                stopTimer();
            } catch (error) {
                if ((error as Error).message === 'RECORDING_HAS_NOT_STARTED') {
                    // if the user pauses too quickly, plugin still thinks a recording has started
                    const recorderResult = await new Promise(resolve => setTimeout(
                        () => resolve(VoiceRecorder.resumeRecording()), 500
                    ));
                    console.log(recorderResult);
                } else {
                    throw error;
                }
            }
        } catch (error) {
            // log to Sentry
            alert.showAndLogError(error);
        }
    };

    const stopRecording = async (opts?: { discard?: boolean, autoSaved?: boolean }) => {
        /*
            Stops the audio recording and returns the recording data.
            When a directory option has been passed to the VoiceRecorder.startRecording method the data will include a uri instead of a recordDataBase64
        */

        const { discard, autoSaved } = {...opts};
        if (discard) {
            const discardRecording = await alert.confirmDestructiveAction({
                destroyLabel: 'Discard',
                cancelLabel: 'No, Continue',
                header: `Delete Recording`,
                message: 'Are you sure you want to discard this recording? It will not be saved.',
            });

            if (discardRecording !== 'destroy'){
                return;
            }
        }

        recordingState.stop();

        try {
            let result: RecordingData;
            try {
                result = await VoiceRecorder.stopRecording();

                if (!discard) {
                    mixpanel.track('Recording Completed', () => {
                        const totalSeconds = Math.round(result.value.msDuration / 1000);
                        const minutes = Math.floor(totalSeconds / 60);
                        const seconds = totalSeconds % 60;
                        return {
                            'Duration': `${minutes < 1 ? '' : minutes+'m' } ${seconds}s`,
                            'Duration Seconds': totalSeconds,
                            'Network Status': networkAdapter.isOnline ? 'online' : 'offline',
                            'Has Appointment': Boolean(recordingState.state.currentRecordingExtras?.appointmentId)
                        };
                    });
                } else {
                    mixpanel.track('Recording Discarded', () => {
                        const totalSeconds = Math.round(result.value.msDuration / 1000);
                        const minutes = Math.floor(totalSeconds / 60);
                        const seconds = totalSeconds % 60;
                        return {
                            'Duration': `${minutes < 1 ? '' : minutes+'m' } ${seconds}s`,
                            'Duration Seconds': totalSeconds
                        };
                    });
                }

                stopTimer();
            } catch (error) {
                if ((error as Error).message === 'RECORDING_HAS_NOT_STARTED') {
                    // if the user stops too quickly, plugin still thinks a recording has started
                    result = await new Promise(resolve => setTimeout(
                        () => resolve(VoiceRecorder.stopRecording()), 500
                    ));
                    console.log(result);
                } else {
                    throw error;
                }
            }

            if (!discard) {
                // remove possible codec that is returned on chrome and firefox
                const mimeType = result.value.mimeType;
                const semiIdx = mimeType.indexOf(';');
                result.value.mimeType = semiIdx > -1 ? mimeType.substring(0, semiIdx) : result.value.mimeType;

                // add() reads summaryPrompt/appointmentId from extras, then clears them
                recordingState.add({
                    mimeType: result.value.mimeType,
                    msDuration: result.value.msDuration,
                    file: base64ToArrayBuffer(result.value.recordDataBase64!)
                }, autoSaved);
            } else {
                // Discard path: add() won't run, so clear extras explicitly
                emitRecordingAborted();
                recordingState.updateCurrentRecordingExtras({ recordingContext: undefined, summaryPrompt: undefined, appointmentId: undefined });
            }
        } catch (error) {
            // log to Sentry
            alert.showAndLogError(error);
        }

        liveUpdatesService.enable();
    };

    const clickHandler = () => {
        ifLoggedIn({
            delegate: async () => {
                // Buttons and forceStartRecording should prevent triggering before ready,
                // but guard here as a safety net to avoid creating the wrong note type
                if (!isReadyToRecord) {
                    return;
                }

                // Acquiring the mic leaves a window where isRecording is still false, so without
                // this a second tap would fall through and kick off a parallel start.
                if (recordingState.isStartingRecording()) {
                    return;
                }

                if (!(await processPermissions())) {
                    emitRecordingAborted();
                    return;
                }

                if (!recordingState.isRecording() && !recordingState.isPaused()) {

                    if (!(await handleInnerhivePlus())) {
                        emitRecordingAborted();
                        return;
                    }

                    const from = recordingState.state.currentRecordingExtras?.initiatedFrom;
                    recordingState.updateCurrentRecordingExtras({ initiatedFrom: undefined });

                    // Single decision point for intro recordings: entry points that don't force
                    // a summaryPrompt of their own get auto-detected here.
                    if (recordingState.shouldUseIntroFlow()) {
                        recordingState.updateCurrentRecordingExtras({
                            summaryPrompt: 'intro'
                        });
                    }

                    const summaryPrompt = recordingState.state.currentRecordingExtras?.summaryPrompt;
                    mixpanel.track('Recording Initiated', () => ({
                        'Initiated From': from || 'Navigation',
                        'Summary Prompt': summaryPrompt || 'none'
                    }));

                    // Check IndexedDB connectivity before starting. Runs ahead of the consent
                    // prompt so an unrecoverable storage failure surfaces its own error screen
                    // instead of landing on top of a consent modal that is still open, and so we
                    // don't ask permission to record when we already know we cannot.
                    if (!(await recordingState.checkStorageConnection())) {
                        emitRecordingAborted();
                        return;
                    }

                    const isIntroRecording = summaryPrompt === 'intro';
                    if (!isIntroRecording && !(await askForConsent())) {
                        emitRecordingAborted();
                        return;
                    }

                    await startRecording();
                } else {
                    recordingState.toggleSheet();
                }

            },
            redirect: history.getCurrentLocation().pathname,
            message: 'Please sign in or create an account to start recording.',
            trackingFrom
        });

    };

    // turn into anPaywallAwareButton (with sparkles?)
    // OR, use the logic in the button to determine if they can record another (5 for free, 6th costs)

    return (
        <>
            <div
                onClick={canRecord ? clickHandler : cannotRecordClickHandler}
                style={{
                    cursor: canRecord || cannotRecordClickHandler ? 'pointer' : undefined,
                    ...((!isReadyToRecord || recordingState.isStartingRecording()) && { pointerEvents: 'none' })
                }}
            >
                {children}
            </div>

            {includeSheet && (
                <RecordingSheet
                    onPause={pauseRecording}
                    onTimerPause={onTimerPause}
                    onResume={resumeRecording}
                    onSave={() => stopRecording()}
                    onCancel={() => stopRecording({discard: true})}
                />
            )}

            <RecordingConsentModal
                key={consentModalKey}
                isOpen={isConsentModalOpen}
                isBusy={recordingState.isStartingRecording()}
                context={getCurrentContext()}
                onAccept={handleConsentAccept}
                onDecline={handleConsentDecline}
                onDidDismiss={handleConsentModalDismiss}
            />
        </>
    );
};
